import React from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Sparkles,
  Target,
  MessageSquare,
  Compass,
  Activity,
  TrendingUp,
  Check,
  Users,
  Zap,
} from "lucide-react";

/**
 * ServiceVisuals — six bespoke animated mini-mockups, one per strategic pillar.
 * Each is a CSS/SVG/framer-motion composition (no external assets).
 * Keyed by the icon name from i18n (Brain, Sparkles, Target, MessageSquare, Compass, Activity).
 */

const ease = [0.22, 1, 0.36, 1];

function Frame({ children, className = "" }) {
  return (
    <div
      className={`relative h-44 sm:h-48 w-full rounded-2xl overflow-hidden border border-white/10 bg-[#0a1120]/80 ${className}`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(23,105,255,0.18),transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(0,0,0,0.4)_100%)]" />
      <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:22px_22px]" />
      {children}
    </div>
  );
}

/* 1) Neuromarketing — pulsing brain with neural connections */
function NeuroVisual() {
  const nodes = [
    { cx: 30, cy: 40 },
    { cx: 70, cy: 30 },
    { cx: 110, cy: 55 },
    { cx: 55, cy: 75 },
    { cx: 95, cy: 90 },
    { cx: 140, cy: 80 },
  ];
  const links = [
    [0, 1], [1, 2], [0, 3], [2, 4], [3, 4], [4, 5], [1, 3], [2, 5],
  ];
  return (
    <Frame>
      <svg viewBox="0 0 170 120" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="brainGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ff7a18" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ff7a18" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="85" cy="60" r="55" fill="url(#brainGlow)" />
        {links.map(([a, b], i) => (
          <motion.line
            key={`l-${a}-${b}`}
            x1={nodes[a].cx}
            y1={nodes[a].cy}
            x2={nodes[b].cx}
            y2={nodes[b].cy}
            stroke="#1769ff"
            strokeWidth="0.8"
            strokeOpacity="0.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: [0, 1, 1, 0] }}
            transition={{ duration: 3.6, repeat: Infinity, delay: i * 0.18, ease }}
          />
        ))}
        {nodes.map((n, i) => (
          <motion.circle
            key={`n-${n.cx}-${n.cy}`}
            cx={n.cx}
            cy={n.cy}
            r={3}
            fill={i % 2 === 0 ? "#ff7a18" : "#1769ff"}
            initial={{ r: 3, opacity: 0.7 }}
            animate={{ r: [2.5, 4.2, 2.5], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.25 }}
          />
        ))}
      </svg>
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <span className="text-[10px] font-mono-tight uppercase tracking-[0.18em] text-white/55">
          Sinapsis activa
        </span>
        <span className="flex items-center gap-1 text-[10px] font-mono-tight text-brand-orange">
          <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
          LIVE
        </span>
      </div>
      <div className="absolute top-3 left-3">
        <Brain className="h-4 w-4 text-white/60" strokeWidth={1.6} />
      </div>
    </Frame>
  );
}

/* 2) AI Estratégica — code/matrix rain with floating sparkles */
function AIVisual() {
  const cols = Array.from({ length: 9 });
  return (
    <Frame>
      <div className="absolute inset-0 flex gap-[6px] px-3 pt-2">
        {cols.map((_, c) => (
          <motion.div
            key={`col-${c}`}
            className="flex-1 font-mono-tight text-[9px] leading-[12px] text-brand-blue/70 whitespace-pre"
            initial={{ y: -40 }}
            animate={{ y: 60 }}
            transition={{
              duration: 4 + (c % 3) * 0.7,
              repeat: Infinity,
              ease: "linear",
              delay: c * 0.18,
            }}
          >
            {"01\n10\n11\n01\n00\n10\n11\n01\n10\n00".split("\n").map((d, i) => (
              <div key={`d-${c}-${i}`} style={{ opacity: 1 - i * 0.08 }}>{d}</div>
            ))}
          </motion.div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1120]" />
      <motion.div
        animate={{ scale: [1, 1.08, 1], rotate: [0, 4, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 grid place-items-center rounded-2xl bg-gradient-to-br from-brand-blue/40 to-brand-blue/10 border border-brand-blue/40 backdrop-blur-md shadow-[0_0_30px_rgba(23,105,255,0.4)]"
      >
        <Sparkles className="h-6 w-6 text-brand-blue" strokeWidth={1.6} />
      </motion.div>
      <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-blue animate-pulse" />
        <span className="text-[10px] font-mono-tight uppercase tracking-[0.18em] text-white/55">
          Modelo · v4.2
        </span>
      </div>
    </Frame>
  );
}

/* 3) Estrategia 360° — animated growth chart with dot trace */
function StrategyVisual() {
  const path = "M10 90 L30 78 L50 82 L70 60 L90 65 L110 40 L130 45 L150 22";
  const dots = [
    { x: 10, y: 90 },
    { x: 30, y: 78 },
    { x: 50, y: 82 },
    { x: 70, y: 60 },
    { x: 90, y: 65 },
    { x: 110, y: 40 },
    { x: 130, y: 45 },
    { x: 150, y: 22 },
  ];
  return (
    <Frame>
      <svg viewBox="0 0 170 110" className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#1769ff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#1769ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* axis grid */}
        {[20, 45, 70, 95].map((y) => (
          <line key={`g-${y}`} x1="5" y1={y} x2="160" y2={y} stroke="#ffffff" strokeOpacity="0.05" />
        ))}
        {/* fill area */}
        <motion.path
          d={`${path} L150 100 L10 100 Z`}
          fill="url(#areaGrad)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, ease }}
        />
        {/* line */}
        <motion.path
          d={path}
          stroke="#1769ff"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.2, ease, repeat: Infinity, repeatType: "reverse" }}
        />
        {dots.map((d, i) => (
          <motion.circle
            key={`d-${d.x}`}
            cx={d.x}
            cy={d.y}
            r={2.4}
            fill="#ff7a18"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }}
          />
        ))}
      </svg>
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <Target className="h-4 w-4 text-white/60" strokeWidth={1.6} />
        <span className="text-[10px] font-mono-tight uppercase tracking-[0.16em] text-white/55">
          KPI · Conversión
        </span>
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-blue/15 border border-brand-blue/30">
        <TrendingUp className="h-3 w-3 text-brand-blue" strokeWidth={2} />
        <span className="text-[10px] font-mono-tight font-bold text-brand-blue">+320%</span>
      </div>
    </Frame>
  );
}

/* 4) WhatsApp + CRM — animated chat bubbles with typing */
function ChatVisual() {
  return (
    <Frame>
      <div className="absolute inset-0 p-4 flex flex-col justify-end gap-2">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="self-start max-w-[70%] px-3 py-2 rounded-2xl rounded-bl-md bg-white/10 backdrop-blur-sm border border-white/10"
        >
          <span className="text-[11px] text-white/85">Hola, quiero saber sobre IA</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="self-end max-w-[78%] px-3 py-2 rounded-2xl rounded-br-md bg-gradient-to-br from-brand-blue to-brand-blue/70 shadow-[0_8px_24px_-12px_rgba(23,105,255,0.6)]"
        >
          <span className="text-[11px] text-white font-medium">¡Hola! Soy tu asistente IA 🤖</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.4 }}
          className="self-end flex items-center gap-1 px-3 py-2 rounded-2xl rounded-br-md bg-gradient-to-br from-brand-blue to-brand-blue/70"
        >
          {[0, 1, 2].map((i) => (
            <motion.span
              key={`dot-${i}`}
              className="h-1.5 w-1.5 rounded-full bg-white"
              animate={{ y: [0, -3, 0], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
            />
          ))}
        </motion.div>
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 grid place-items-center">
          <MessageSquare className="h-3 w-3 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] font-semibold text-white/85">WhatsApp IA</span>
          <span className="text-[8.5px] font-mono-tight text-green-400 flex items-center gap-1">
            <span className="h-1 w-1 rounded-full bg-green-400 animate-pulse" /> en línea
          </span>
        </div>
      </div>
    </Frame>
  );
}

/* 5) Posicionamiento — animated brand compass with orbiting tags */
function BrandVisual() {
  const tags = ["VALOR", "MARCA", "VOZ", "PROMESA"];
  return (
    <Frame>
      <div className="absolute inset-0 grid place-items-center">
        <div className="relative h-32 w-32">
          {/* outer ring */}
          <motion.div
            className="absolute inset-0 rounded-full border border-brand-orange/30"
            animate={{ rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          >
            {tags.map((tag, i) => {
              const angle = (i * 360) / tags.length;
              return (
                <div
                  key={tag}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  style={{ transform: `translate(-50%,-50%) rotate(${angle}deg) translateY(-58px)` }}
                >
                  <motion.span
                    className="block px-1.5 py-0.5 rounded-md bg-[#0a1120] border border-brand-orange/40 text-[8.5px] font-mono-tight tracking-[0.16em] text-brand-orange whitespace-nowrap"
                    style={{ transform: `rotate(-${angle}deg)` }}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
                  >
                    {tag}
                  </motion.span>
                </div>
              );
            })}
          </motion.div>
          {/* inner ring */}
          <motion.div
            className="absolute inset-4 rounded-full border border-brand-blue/40 border-dashed"
            animate={{ rotate: -360 }}
            transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
          />
          {/* center */}
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2.4, repeat: Infinity }}
            className="absolute inset-0 grid place-items-center"
          >
            <div className="h-12 w-12 grid place-items-center rounded-full bg-gradient-to-br from-brand-orange via-brand-orange/80 to-brand-blue shadow-[0_0_30px_rgba(255,122,24,0.5)]">
              <Compass className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
        <span className="text-[10px] font-mono-tight uppercase tracking-[0.18em] text-white/55">
          Eje de marca
        </span>
        <span className="text-[10px] font-mono-tight text-brand-orange">360°</span>
      </div>
    </Frame>
  );
}

/* 6) Coaching — connected avatars with progress checklist */
function CoachingVisual() {
  const steps = [
    { label: "Diagnóstico", done: true },
    { label: "Sistema", done: true },
    { label: "Ejecución", done: false },
  ];
  return (
    <Frame>
      <div className="absolute inset-0 p-4 flex flex-col gap-3 justify-center">
        {/* avatars row */}
        <div className="flex items-center gap-2 mb-1">
          <div className="flex -space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={`av-${i}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                className={`h-7 w-7 rounded-full border-2 border-[#0a1120] grid place-items-center ${
                  i === 0
                    ? "bg-gradient-to-br from-brand-blue to-brand-blue/60"
                    : i === 1
                    ? "bg-gradient-to-br from-brand-orange to-brand-orange/60"
                    : "bg-gradient-to-br from-white/30 to-white/10"
                }`}
              >
                <Users className="h-3 w-3 text-white" strokeWidth={2} />
              </motion.div>
            ))}
          </div>
          <span className="text-[10px] font-mono-tight uppercase tracking-[0.16em] text-white/55 ml-1">
            1:1 con estratega
          </span>
        </div>

        {/* checklist */}
        <div className="space-y-1.5">
          {steps.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.2, duration: 0.5 }}
              className="flex items-center gap-2"
            >
              <div
                className={`h-4 w-4 rounded-md grid place-items-center border ${
                  s.done
                    ? "bg-brand-blue/20 border-brand-blue/50"
                    : "bg-white/5 border-white/15"
                }`}
              >
                {s.done && <Check className="h-2.5 w-2.5 text-brand-blue" strokeWidth={3} />}
              </div>
              <span
                className={`text-[11px] ${
                  s.done ? "text-white/85 line-through decoration-brand-blue/60" : "text-white/55"
                }`}
              >
                {s.label}
              </span>
              {!s.done && (
                <motion.span
                  className="ml-auto h-1 w-12 rounded-full bg-white/10 overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <motion.span
                    className="block h-full bg-gradient-to-r from-brand-orange to-brand-orange/60"
                    initial={{ width: "0%" }}
                    animate={{ width: ["0%", "65%", "65%"] }}
                    transition={{ duration: 2.5, repeat: Infinity, repeatType: "reverse" }}
                  />
                </motion.span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-orange/15 border border-brand-orange/30">
        <Zap className="h-3 w-3 text-brand-orange" strokeWidth={2.2} />
        <span className="text-[10px] font-mono-tight font-bold text-brand-orange">EN VIVO</span>
      </div>
      <div className="absolute top-3 left-3">
        <Activity className="h-4 w-4 text-white/60" strokeWidth={1.6} />
      </div>
    </Frame>
  );
}

const VISUAL_MAP = {
  Brain: NeuroVisual,
  Sparkles: AIVisual,
  Target: StrategyVisual,
  MessageSquare: ChatVisual,
  Compass: BrandVisual,
  Activity: CoachingVisual,
};

export default function ServiceVisual({ iconName }) {
  const V = VISUAL_MAP[iconName];
  if (!V) return null;
  return <V />;
}
